import { ref, onMounted, onUnmounted } from 'vue';

/**
 * Composable for lazy loading images with IntersectionObserver
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Visibility threshold (0-1), default 0.1
 * @param {string} options.rootMargin - Root margin for intersection, default '50px'
 */
export function useLazyImage(options = {}) {
  const { threshold = 0.1, rootMargin = '50px' } = options;

  const isLoaded = ref(false);
  const isInView = ref(false);
  const hasError = ref(false);
  const imageRef = ref(null);

  let observer = null;

  function setupObserver(element) {
    if (!element) return;

    if (!('IntersectionObserver' in window)) {
      // Fallback for browsers without IntersectionObserver
      isInView.value = true;
      return;
    }

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            isInView.value = true;
            if (observer) {
              observer.unobserve(entry.target);
            }
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
  }

  function onLoad() {
    isLoaded.value = true;
  }

  function onError() {
    hasError.value = true;
  }

  function cleanup() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  }

  return {
    isLoaded,
    isInView,
    hasError,
    imageRef,
    setupObserver,
    onLoad,
    onError,
    cleanup
  };
}

/**
 * Directive for lazy loading images
 * Usage: v-lazy-src="imageUrl"
 */
export const vLazySrc = {
  mounted(el, binding) {
    const loadImage = () => {
      el.src = binding.value;
    };

    if (!('IntersectionObserver' in window)) {
      loadImage();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadImage();
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    observer.observe(el);

    // Store observer for cleanup
    el._lazyObserver = observer;
  },
  unmounted(el) {
    if (el._lazyObserver) {
      el._lazyObserver.disconnect();
      delete el._lazyObserver;
    }
  },
  updated(el, binding) {
    if (binding.value !== binding.oldValue) {
      el.src = binding.value;
    }
  }
};

export default useLazyImage;
